import api from './api';

let settingsCache = null;
let loadPromise = null;
const saveQueues = {};
const PUBLIC_PATHS = ['/'];

const isPublicRoute = () => PUBLIC_PATHS.includes(window.location.pathname);

export const userSettingsService = {
  async loadAll() {
    // On public routes (login) the user isn't authenticated yet, so return an
    // ephemeral empty object WITHOUT caching it. Caching {} here would poison the
    // module-level cache: after login, loadAll() would short-circuit on the stale
    // {} and never fetch the real settings, making saved preferences (visible
    // columns, theme, etc.) appear to reset on every login.
    if (isPublicRoute()) return {};
    if (settingsCache) return settingsCache;
    if (!loadPromise) {
      loadPromise = api.get('/user-settings', { skipAuthRedirect: true })
        .then(({ data }) => {
          settingsCache = data.settings || {};
          return settingsCache;
        })
        .catch(() => {
          settingsCache = {};
          return settingsCache;
        })
        .finally(() => {
          loadPromise = null;
        });
    }
    return loadPromise;
  },

  getCached(key, fallback) {
    return settingsCache && Object.prototype.hasOwnProperty.call(settingsCache, key)
      ? settingsCache[key]
      : fallback;
  },

  async get(key, fallback) {
    const settings = await this.loadAll();
    return Object.prototype.hasOwnProperty.call(settings, key) ? settings[key] : fallback;
  },

  async set(key, value) {
    if (isPublicRoute()) return value;
    settingsCache = { ...(settingsCache || {}), [key]: value };
    const previousSave = saveQueues[key] || Promise.resolve();
    const save = previousSave
      .catch(() => {})
      .then(() => api.put(`/user-settings/${encodeURIComponent(key)}`, { value }, { skipAuthRedirect: true }));
    const queuedSave = save.finally(() => {
      if (saveQueues[key] === queuedSave) delete saveQueues[key];
    });
    saveQueues[key] = queuedSave;
    await save;
    return value;
  },

  clearCache() {
    settingsCache = null;
    loadPromise = null;
  },
};
